import { Controller } from "@hotwired/stimulus"
import { sessionIdFromCookies } from "../lib/ga4_cookie.js"

export default class extends Controller {
    static targets = ["recaptchaContainer", "responseInput", "gaIdInput", "sessionIdInput", "error"]
    static values = { sitekey: String, measurementId: String }

    connect() {
        this.widgetId = null
        this.apiReadyHandler = this.renderRecaptcha.bind(this)
        this.windowLoadHandler = this.populateGaId.bind(this)
        this.submitHandler = this.onSubmit.bind(this)
        this.gaIdRetryCount = 0

        this.populateGaId()

        if (this.apiLoaded()) {
            this.renderRecaptcha()
        }

        document.addEventListener("recaptcha:api-ready", this.apiReadyHandler)
        window.addEventListener("load", this.windowLoadHandler)

        // 空トークンのままPOSTされる取りこぼしを防ぐため、フォーム送信を検証する
        this.form = this.element.closest("form")
        if (this.form) {
            this.form.addEventListener("submit", this.submitHandler)
        }
    }

    disconnect() {
        document.removeEventListener("recaptcha:api-ready", this.apiReadyHandler)
        window.removeEventListener("load", this.windowLoadHandler)
        if (this.form) {
            this.form.removeEventListener("submit", this.submitHandler)
        }
    }

    onSubmit(event) {
        // GTM の GA4 タグが cookie を付けるのが接続時のリトライ(最大5回)より遅かった場合に備え、送信直前にも cookie から読み直す
        this.fillGaIdsFromCookie()

        if (this.tokenPresent()) {
            this.clearError()
            return
        }

        // トークンが空なら送信を止め、原因に応じたメッセージを出す
        event.preventDefault()
        if (this.widgetId === null) {
            // ウィジェット未描画（api.js未ロード・遮断等）。再描画を試みる
            this.renderRecaptcha()
            this.setError("reCAPTCHAを読み込めませんでした。少し待つか、ページを再読み込みしてください。")
        } else {
            this.setError("「私はロボットではありません」にチェックを入れてください。")
        }
    }

    tokenPresent() {
        if (this.widgetId !== null && this.apiLoaded()) {
            return Boolean(window.grecaptcha.getResponse(this.widgetId))
        }
        return this.hasResponseInputTarget && Boolean(this.responseInputTarget.value)
    }

    setError(message) {
        if (this.hasErrorTarget) {
            this.errorTarget.textContent = message
            this.errorTarget.classList.remove("hidden")
        }
    }

    clearError() {
        if (this.hasErrorTarget) {
            this.errorTarget.textContent = ""
            this.errorTarget.classList.add("hidden")
        }
    }

    apiLoaded() {
        return typeof window !== "undefined" && typeof window.grecaptcha !== "undefined"
    }

    renderRecaptcha() {
        if (!this.hasRecaptchaContainerTarget || this.widgetId !== null || !this.apiLoaded()) {
            return
        }

        window.grecaptcha.ready(() => {
            if (this.widgetId !== null) return

            this.widgetId = window.grecaptcha.render(this.recaptchaContainerTarget, {
                sitekey: this.sitekeyValue,
                callback: this.recaptchaCallback.bind(this),
                // Add other reCAPTCHA parameters as needed (e.g., 'theme', 'size')
            })
        })
    }

    recaptchaCallback(response) {
        if (this.hasResponseInputTarget) {
            this.responseInputTarget.value = response || ""
        }

        if (response) {
            this.clearError()
        }

        this.dispatch("recaptcha:solved", { detail: { response } })
    }

    reset() {
        if (this.widgetId !== null && this.apiLoaded()) {
            window.grecaptcha.reset(this.widgetId)
        }
    }

    populateGaId() {
        if ((!this.hasGaIdInputTarget || this.gaIdInputTarget.value) && (!this.hasSessionIdInputTarget || this.sessionIdInputTarget.value)) {
            return
        }

        this.fillGaIdsFromCookie()
        this.requestIdsFromGtag()

        const hasGaId = !this.hasGaIdInputTarget || this.gaIdInputTarget.value
        const hasSessionId = !this.hasSessionIdInputTarget || this.sessionIdInputTarget.value

        if ((!hasGaId || !hasSessionId) && this.gaIdRetryCount < 5) {
            this.gaIdRetryCount += 1
            setTimeout(() => this.populateGaId(), this.gaIdRetryCount * 300)
        }
    }

    // 空の hidden 欄だけを cookie(_ga / _ga_<ID>)の値で埋める。入力済みの値は上書きしない
    fillGaIdsFromCookie() {
        if (this.hasGaIdInputTarget && !this.gaIdInputTarget.value) {
            const clientIdFromCookie = this.readClientIdFromCookie()
            if (clientIdFromCookie) {
                this.gaIdInputTarget.value = clientIdFromCookie
            }
        }

        if (this.hasSessionIdInputTarget && !this.sessionIdInputTarget.value) {
            const sessionIdFromCookie = this.readSessionIdFromCookie()
            if (sessionIdFromCookie) {
                this.sessionIdInputTarget.value = sessionIdFromCookie
            }
        }
    }

    requestIdsFromGtag() {
        if (typeof window === "undefined" || typeof window.gtag !== "function") {
            return
        }

        if (!this.hasMeasurementIdValue || !this.measurementIdValue) {
            return
        }

        try {
            window.gtag("get", this.measurementIdValue, "client_id", (clientId) => {
                if (clientId && this.hasGaIdInputTarget && !this.gaIdInputTarget.value) {
                    this.gaIdInputTarget.value = clientId
                }
            })

            window.gtag("get", this.measurementIdValue, "session_id", (sessionId) => {
                if (sessionId && this.hasSessionIdInputTarget && !this.sessionIdInputTarget.value) {
                    this.sessionIdInputTarget.value = String(sessionId)
                }
            })
        } catch (_error) {
        }
    }

    readClientIdFromCookie() {
        if (typeof document === "undefined") {
            return null
        }

        const gaCookie = document.cookie
            .split("; ")
            .find((entry) => entry.startsWith("_ga="))

        if (!gaCookie) {
            return null
        }

        const cookieValue = decodeURIComponent(gaCookie.split("=")[1] || "")
        const match = cookieValue.match(/^GA\d+\.\d+\.(.+)\.(.+)$/)

        if (!match) {
            return null
        }

        return `${match[1]}.${match[2]}`
    }

    readSessionIdFromCookie() {
        if (typeof document === "undefined" || !this.hasMeasurementIdValue || !this.measurementIdValue) {
            return null
        }

        return sessionIdFromCookies(document.cookie, this.measurementIdValue)
    }
}
